<template>
  <div>
    <h2
      class="mt-0 tw-text-2xl tw-font-semibold tw-mb-2"
      style="color: var(--text-color)"
    >
      Snippets
    </h2>

    <SnippetsConfirmDialog />

    <TabView
      :activeIndex="activeTabIndex"
      @tab-change="(e) => setTab(tabs[e.index])"
    >
      <TabPanel header="Library">
        <Card>
          <template #header>
            <div
              class="p-3 pb-0 flex flex-column sm:flex-row justify-content-between align-items-start sm:align-items-center gap-3"
            >
              <Button
                icon="pi pi-refresh"
                text
                rounded
                aria-label="Refresh"
                v-tooltip.bottom="'Refresh'"
                :loading="loading"
                @click="refreshSnippets"
              />
              <div class="flex flex-column sm:flex-row gap-3">
                <div class="flex align-items-center gap-1 w-full sm:w-20rem">
                  <Calendar
                    input-id="snippets-date-range"
                    v-model="dateRange"
                    :showIcon="true"
                    :number-of-months="1"
                    :selectionMode="'range'"
                    placeholder="Search by date range"
                    class="w-full"
                    @update:modelValue="setDateRange"
                  />
                  <i
                    v-if="dateRange"
                    class="pi pi-times cursor-pointer"
                    v-tooltip.bottom="'Clear date range'"
                    @click="clearDateRange"
                  ></i>
                </div>
                <InputText
                  class="w-full sm:w-24rem"
                  type="text"
                  v-model="searchQuery"
                  @input="onSearchInput"
                  placeholder="Search show or episode"
                />
              </div>
            </div>
          </template>
          <template #content>
            <div class="overflow-x-auto">
              <DataTable
                :value="snippets"
                class="p-datatable-sm"
                lazy
                :paginator="true"
                :rows="10"
                :total-records="totalSnippets"
                :loading="loading"
                @page="onPage"
              >
                <Column field="id" header="ID">
                  <template #body="slotProps">
                    <span class="text-500 text-sm">{{
                      slotProps.data.id
                    }}</span>
                  </template>
                </Column>

                <Column
                  field="show_title"
                  header="Show"
                  style="min-width: 127px"
                />

                <Column field="episode_title" header="Episode">
                  <template #body="slotProps">
                    <span
                      class="episode-title-cell"
                      v-tooltip.top="slotProps.data.episode_title"
                    >
                      {{ slotProps.data.episode_title }}
                    </span>
                  </template>
                </Column>

                <Column header="Audio">
                  <template #body="slotProps">
                    <audio
                      controls
                      preload="none"
                      class="snippet-audio-player"
                      :src="slotProps.data.snippet_audio_url"
                    />
                    <div
                      v-if="
                        slotProps.data.metadata?.clean_audio_url &&
                        slotProps.data.metadata.clean_audio_url !==
                          slotProps.data.snippet_audio_url
                      "
                      class="flex align-items-center gap-2 text-xs"
                    >
                      <span class="text-500">
                        playing: with fade
                        {{ slotProps.data.metadata.fade_out_sec }}s
                      </span>
                      <a
                        :href="slotProps.data.metadata.clean_audio_url"
                        target="_blank"
                        rel="noopener"
                        >compare original</a
                      >
                    </div>
                  </template>
                </Column>

                <Column field="duration_sec" header="Duration">
                  <template #body="slotProps">
                    {{ slotProps.data.duration_sec }}s
                  </template>
                </Column>

                <Column header="Transcript">
                  <template #body="slotProps">
                    <Button
                      v-if="slotProps.data.metadata?.transcript_text"
                      label="View"
                      size="small"
                      text
                      @click="openTranscript(slotProps.data)"
                    />
                    <span v-else class="text-500 text-sm">None</span>
                  </template>
                </Column>

                <Column header="Validation Note" style="min-width: 118px">
                  <template #body="slotProps">
                    <ValidationNoteCell
                      :text="slotProps.data.validation_note"
                    />
                  </template>
                </Column>

                <Column
                  field="created_at"
                  header="Created At"
                  style="min-width: 206px"
                >
                  <template #body="slotProps">
                    {{ formatDate(slotProps.data.created_at) }}
                  </template>
                </Column>

                <Column field="is_disabled">
                  <template #header>
                    <span class="flex align-items-center" style="gap: 5px">
                      Disabled
                      <i
                        class="pi pi-info-circle text-500"
                        v-tooltip.top="
                          'Disabled snippets are excluded from playlists for sellers with restricted validation'
                        "
                      />
                    </span>
                  </template>
                  <template #body="slotProps">
                    <div class="text-center">
                      {{ slotProps.data.is_disabled ? "Yes" : "No" }}
                    </div>
                  </template>
                </Column>

                <Column header="Actions">
                  <template #body="slotProps">
                    <div class="flex align-items-center gap-2">
                      <RegenerationStatusCell
                        :regenerating="slotProps.data.regenerating"
                        :just-regenerated="
                          justRegeneratedIds.has(slotProps.data.id)
                        "
                        :loading="regeneratingClickedId === slotProps.data.id"
                        @regenerate="confirmRegenerateSnippet(slotProps.data)"
                      />
                      <Button
                        icon="pi pi-video"
                        v-tooltip.top="
                          slotProps.data.has_video
                            ? 'View in Social Library'
                            : slotProps.data.video_generating
                            ? 'Generating video…'
                            : 'Generate video'
                        "
                        outlined
                        rounded
                        size="small"
                        class="round-icon-btn"
                        :severity="
                          !slotProps.data.has_video &&
                          !slotProps.data.video_generating
                            ? 'success'
                            : undefined
                        "
                        :loading="
                          slotProps.data.video_generating ||
                          generatingVideoClickedId === slotProps.data.snippet_id
                        "
                        @click="
                          slotProps.data.has_video
                            ? viewVideoForSnippet(slotProps.data)
                            : confirmGenerateVideo(slotProps.data)
                        "
                        :aria-label="
                          slotProps.data.has_video
                            ? 'View in Social Library'
                            : 'Generate video'
                        "
                      />
                      <Button
                        :label="
                          slotProps.data.is_disabled ? 'Enable' : 'Disable'
                        "
                        :severity="
                          slotProps.data.is_disabled ? 'success' : 'danger'
                        "
                        size="small"
                        outlined
                        class="snippet-toggle-btn"
                        @click="
                          toggleSnippetEnabled(
                            slotProps.data,
                            !!slotProps.data.is_disabled
                          )
                        "
                      />
                    </div>
                  </template>
                </Column>
              </DataTable>
            </div>
          </template>
        </Card>
      </TabPanel>

      <TabPanel header="Shows">
        <Card>
          <template #header>
            <div
              class="p-3 pb-0 flex justify-content-between align-items-center"
            >
              <Button
                icon="pi pi-refresh"
                text
                rounded
                aria-label="Refresh"
                v-tooltip.bottom="'Refresh'"
                @click="fetchShows"
              />
              <Button
                label="Add Show"
                icon="pi pi-plus"
                size="small"
                @click="openAddShowDialog"
              />
            </div>
          </template>
          <template #content>
            <DataTable
              :value="shows"
              class="p-datatable-sm"
              :paginator="true"
              :rows="10"
              :loading="showsLoading"
            >
              <Column field="show_title" header="Show" :sortable="true" />

              <Column header="Duration">
                <template #body="slotProps">
                  <span v-if="slotProps.data.snippets_generation_config">
                    {{ slotProps.data.snippets_generation_config.duration }}s
                  </span>
                  <span v-else class="text-500 text-sm">—</span>
                </template>
              </Column>

              <Column header="Fade-out">
                <template #body="slotProps">
                  <span
                    v-if="
                      slotProps.data.snippets_generation_config?.fade_out_sec
                    "
                  >
                    {{
                      slotProps.data.snippets_generation_config.fade_out_sec
                    }}s
                  </span>
                  <span v-else class="text-500 text-sm">—</span>
                </template>
              </Column>

              <Column header="Snippets/Episode">
                <template #body="slotProps">
                  <span v-if="slotProps.data.snippets_generation_config">
                    {{
                      slotProps.data.snippets_generation_config
                        .snippets_per_episode
                    }}
                  </span>
                  <span v-else class="text-500 text-sm">—</span>
                </template>
              </Column>

              <Column
                field="snippet_count"
                header="Snippets Generated"
                :sortable="true"
              >
                <template #body="slotProps">
                  <Badge
                    :severity="
                      slotProps.data.snippet_count > 0 ? 'success' : 'secondary'
                    "
                    :value="slotProps.data.snippet_count"
                  />
                </template>
              </Column>

              <Column
                field="last_snippet_at"
                header="Last Snippet Generated"
                :sortable="true"
              >
                <template #body="slotProps">
                  <span v-if="slotProps.data.last_snippet_at">
                    {{ formatDate(slotProps.data.last_snippet_at) }}
                  </span>
                  <span v-else class="text-500 text-sm">Never</span>
                </template>
              </Column>

              <Column
                field="active_seller_count"
                header="Active Sellers"
                :sortable="true"
              >
                <template #body="slotProps">
                  <div class="flex align-items-center gap-2">
                    <Badge
                      :severity="
                        slotProps.data.active_seller_count > 0
                          ? 'info'
                          : 'secondary'
                      "
                      :value="slotProps.data.active_seller_count"
                    />
                    <span
                      v-if="slotProps.data.active_seller_count === 0"
                      class="text-500 text-sm white-space-nowrap"
                      v-tooltip.top="
                        'No active seller uses this show, so new episodes are not queued automatically'
                      "
                    >
                      Auto-gen paused
                    </span>
                  </div>
                </template>
              </Column>

              <Column header="Generate Video Assets">
                <template #body="slotProps">
                  <Badge
                    :severity="
                      slotProps.data.snippets_generation_config
                        ?.video_generation_enabled
                        ? 'success'
                        : 'secondary'
                    "
                    :value="
                      slotProps.data.snippets_generation_config
                        ?.video_generation_enabled
                        ? 'Yes'
                        : 'No'
                    "
                  />
                </template>
              </Column>

              <Column>
                <template #body="slotProps">
                  <Button
                    icon="pi pi-eye"
                    v-tooltip.left="
                      slotProps.data.snippet_count > 0
                        ? 'View Snippets'
                        : 'No snippets generated yet'
                    "
                    outlined
                    rounded
                    size="small"
                    class="mr-2 round-icon-btn"
                    :disabled="slotProps.data.snippet_count === 0"
                    @click="viewSnippetsForShow(slotProps.data)"
                    aria-label="View Snippets"
                  />
                  <Button
                    icon="pi pi-video"
                    v-tooltip.left="
                      slotProps.data.social_library_video_assets_count > 0
                        ? 'View in Social Library'
                        : 'No videos generated yet'
                    "
                    outlined
                    rounded
                    size="small"
                    class="mr-2 round-icon-btn"
                    :disabled="
                      !slotProps.data.social_library_video_assets_count
                    "
                    @click="viewVideosForShow(slotProps.data)"
                    aria-label="View in Social Library"
                  />
                  <Button
                    icon="pi pi-plus"
                    v-tooltip.left="'Generate One More Snippet'"
                    outlined
                    rounded
                    severity="success"
                    size="small"
                    class="mr-2 round-icon-btn"
                    :loading="
                      generatingOneMoreShowId === slotProps.data.show_id
                    "
                    @click="confirmGenerateOneMoreSnippet(slotProps.data)"
                    aria-label="Generate One More Snippet"
                  />
                  <Button
                    icon="pi pi-pencil"
                    v-tooltip.left="'Edit Snippet Config'"
                    outlined
                    rounded
                    size="small"
                    class="mr-2 round-icon-btn"
                    @click="openEditShowDialog(slotProps.data)"
                    aria-label="Edit Snippet Config"
                  />
                  <Button
                    icon="pi pi-trash"
                    v-tooltip.left="
                      slotProps.data.active_seller_count > 0
                        ? 'Cannot remove: sellers still active for this show'
                        : 'Remove Show'
                    "
                    outlined
                    rounded
                    severity="danger"
                    size="small"
                    class="round-icon-btn"
                    :disabled="slotProps.data.active_seller_count > 0"
                    @click="confirmRemoveShow(slotProps.data)"
                    aria-label="Remove Show"
                  />
                </template>
              </Column>
            </DataTable>
          </template>
        </Card>
      </TabPanel>
    </TabView>

    <TranscriptDialog
      v-model:visible="showTranscriptDialog"
      :text="selectedTranscript"
    />

    <Dialog
      v-model:visible="showAddShowDialog"
      :header="editingShow ? 'Edit Snippet Config' : 'Add Show'"
      modal
      :style="{ width: '30rem' }"
    >
      <div ref="addShowStepWrapper" class="add-show-step-wrapper">
        <div v-if="!showAddShowConfirmStep" class="flex flex-column gap-3">
          <div class="flex flex-column gap-1">
            <label v-if="!editingShow" for="addShowDropdown">Show</label>
            <Dropdown
              v-if="!editingShow"
              id="addShowDropdown"
              v-model="showToAdd"
              :options="availableShowsToAdd"
              optionLabel="show_title"
              filter
              placeholder="Select a show"
              class="w-full"
              :panelStyle="{
                width: 'min(26rem, 96vw)',
                maxHeight: '16rem',
                overflow: 'hidden',
              }"
              :loading="availableShowsLoading"
            />
            <template v-else>
              <label>Show</label>
              <div class="p-2 border-round surface-100 font-medium">
                {{ editingShow.show_title }}
              </div>
            </template>
          </div>

          <div class="flex flex-column gap-1">
            <label for="snippetDuration">Snippet Length (seconds)</label>
            <InputNumber
              id="snippetDuration"
              v-model="newShowDuration"
              class="w-full"
              :class="{ 'p-invalid': durationError }"
            />
            <small :class="durationError ? 'p-error' : 'text-500'">
              {{ durationError || "Between 60 and 180 seconds." }}
            </small>
          </div>

          <div class="flex flex-column gap-1">
            <label for="snippetsPerEpisode">Snippets per Episode</label>
            <InputNumber
              id="snippetsPerEpisode"
              v-model="newShowSnippetsPerEpisode"
              :min="1"
              :max="5"
              class="w-full"
              :class="{ 'p-invalid': snippetsPerEpisodeError }"
            />
            <small v-if="snippetsPerEpisodeError" class="p-error">
              {{ snippetsPerEpisodeError }}
            </small>
          </div>

          <div class="flex flex-column gap-1">
            <Button
              :label="showAdvancedAudio ? 'Hide advanced' : 'Advanced'"
              :icon="
                showAdvancedAudio ? 'pi pi-chevron-up' : 'pi pi-chevron-down'
              "
              text
              class="align-self-start p-0"
              @click="showAdvancedAudio = !showAdvancedAudio"
            />

            <template v-if="showAdvancedAudio">
              <div class="flex flex-column gap-1">
                <label for="snippetFadeOut">Fade-out (seconds)</label>
                <InputNumber
                  id="snippetFadeOut"
                  v-model="newShowFadeOut"
                  :minFractionDigits="1"
                  :maxFractionDigits="1"
                  class="w-full"
                  :class="{ 'p-invalid': fadeOutError }"
                />
                <small :class="fadeOutError ? 'p-error' : 'text-500'">
                  {{ fadeOutError || "Baked into new snippets only. 0 = off." }}
                </small>
              </div>
            </template>
          </div>

          <div class="flex flex-column gap-1">
            <label>Generate Video Assets</label>
            <div class="flex align-items-center gap-2">
              <InputSwitch
                inputId="videoGenerationEnabled"
                v-model="newShowVideoGenerationEnabled"
              />
              <label for="videoGenerationEnabled" class="cursor-pointer">
                Generate video assets for each snippet
              </label>
            </div>
          </div>

          <div v-if="!editingShow" class="flex flex-column gap-1">
            <label>Episode Range for First Run</label>
            <div class="flex gap-3">
              <div
                class="episode-range-option flex align-items-center gap-2 px-3 py-2 border-round-3xl cursor-pointer"
                :class="{
                  'episode-range-option-active':
                    newShowEpisodesRangeMode === 'last',
                }"
                @click="newShowEpisodesRangeMode = 'last'"
              >
                <RadioButton
                  inputId="episodesRangeLast"
                  :modelValue="newShowEpisodesRangeMode"
                  value="last"
                  name="episodesRangeMode"
                  @update:modelValue="newShowEpisodesRangeMode = $event"
                />
                <i class="pi pi-list episode-range-icon"></i>
                <label for="episodesRangeLast" class="cursor-pointer"
                  >Last N Episodes</label
                >
              </div>
              <div
                class="episode-range-option flex align-items-center gap-2 px-3 py-2 border-round-3xl cursor-pointer"
                :class="{
                  'episode-range-option-active':
                    newShowEpisodesRangeMode === 'all',
                }"
                @click="newShowEpisodesRangeMode = 'all'"
              >
                <RadioButton
                  inputId="episodesRangeAll"
                  :modelValue="newShowEpisodesRangeMode"
                  value="all"
                  name="episodesRangeMode"
                  @update:modelValue="newShowEpisodesRangeMode = $event"
                />
                <i class="pi pi-infinity episode-range-icon"></i>
                <label for="episodesRangeAll" class="cursor-pointer"
                  >All Episodes</label
                >
              </div>
            </div>
            <div
              v-if="newShowEpisodesRangeMode === 'last'"
              class="flex align-items-center gap-2 mt-2"
            >
              <span>Last</span>
              <InputNumber
                v-model="newShowEpisodesRangeCount"
                :min="1"
                :max="99"
                :useGrouping="false"
                class="episode-range-count-input"
              />
              <span>episodes</span>
            </div>
          </div>
        </div>

        <div v-else class="flex flex-column gap-3">
          <p class="m-0">{{ addShowConfirmMessage }}</p>
        </div>
      </div>
      <template #footer>
        <Button
          v-if="!showAddShowConfirmStep"
          label="Cancel"
          outlined
          :disabled="addShowSubmitting"
          @click="showAddShowDialog = false"
        />
        <Button
          v-else
          label="Back"
          outlined
          :disabled="addShowSubmitting"
          @click="cancelAddShowConfirmStep"
        />

        <Button
          v-if="editingShow"
          label="Save"
          :loading="addShowSubmitting"
          :disabled="snippetConfigInvalid"
          @click="saveShowConfig"
        />
        <Button
          v-else-if="!showAddShowConfirmStep"
          label="Add"
          :disabled="!showToAdd || snippetConfigInvalid"
          @click="goToAddShowConfirmStep"
        />
        <Button
          v-else
          label="Yes, Continue"
          :loading="addShowSubmitting"
          @click="addShow"
        />
      </template>
    </Dialog>
  </div>
</template>

<script>
import { ref, computed, onMounted, watch, nextTick } from "vue";
import Card from "primevue/card";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import Dialog from "primevue/dialog";
import InputText from "primevue/inputtext";
import InputNumber from "primevue/inputnumber";
import RadioButton from "primevue/radiobutton";
import InputSwitch from "primevue/inputswitch";
import Dropdown from "primevue/dropdown";
import Badge from "primevue/badge";
import TabView from "primevue/tabview";
import TabPanel from "primevue/tabpanel";
import Calendar from "primevue/calendar";
import { formatDate } from "../../helpers";
import TranscriptDialog from "./TranscriptDialog.vue";
import RegenerationStatusCell from "./shared/RegenerationStatusCell.vue";
import ValidationNoteCell from "./shared/ValidationNoteCell.vue";
import SnippetsConfirmDialog from "./shared/SnippetsConfirmDialog.vue";
import { useSnippetsLibrary } from "./composables/useSnippetsLibrary";
import { useSnippetShows } from "./composables/useSnippetShows";
import { useTabState } from "@/composables/useTabState";
import { useRoute, useRouter } from "vue-router";

export default {
  components: {
    Card,
    DataTable,
    Column,
    Button,
    Dialog,
    InputText,
    InputNumber,
    RadioButton,
    InputSwitch,
    Dropdown,
    Badge,
    TabView,
    TabPanel,
    Calendar,
    TranscriptDialog,
    RegenerationStatusCell,
    ValidationNoteCell,
    SnippetsConfirmDialog,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const token = localStorage.getItem("adminToken");
    // ?tab=shows in the URL, so a reload lands on the tab you were on.
    const tabs = ["library", "shows"];
    const { tab, setTab } = useTabState(tabs, "library");
    const activeTabIndex = computed(() => tabs.indexOf(tab.value));

    const library = useSnippetsLibrary(token);
    const showsTab = useSnippetShows(token);

    const viewSnippetsForShow = (show) => {
      library.searchQuery.value = show.show_title;
      library.currentPage.value = 1;
      setTab("library");
    };

    const viewVideosForShow = (show) => {
      router.push({
        path: "/admin/social-library",
        query: { showId: show.show_id },
      });
    };

    const viewVideoForSnippet = (snippet) => {
      router.push({
        path: "/admin/social-library",
        query: { snippetId: snippet.snippet_id },
      });
    };

    const addShowStepWrapper = ref(null);

    watch(showsTab.showAddShowConfirmStep, async () => {
      const el = addShowStepWrapper.value;
      if (!el) return;

      const startHeight = el.offsetHeight;
      await nextTick();

      el.style.height = "auto";
      const endHeight = el.scrollHeight;
      el.style.height = `${startHeight}px`;

      requestAnimationFrame(() => {
        el.style.height = `${endHeight}px`;
      });
      setTimeout(() => {
        el.style.height = "auto";
      }, 260);
    });

    watch(activeTabIndex, (index) => {
      if (index === 0) {
        library.fetchSnippets();
      } else if (index === 1) {
        library.resetFilters();
        showsTab.fetchShows();
      }
    });

    onMounted(() => {
      if (route.query.snippetId) {
        library.snippetIdFilter.value = route.query.snippetId;
      }
      if (activeTabIndex.value === 0) library.fetchSnippets();
      else showsTab.fetchShows();
    });

    return {
      tabs,
      setTab,
      activeTabIndex,
      viewSnippetsForShow,
      viewVideosForShow,
      viewVideoForSnippet,
      addShowStepWrapper,
      formatDate,
      ...library,
      ...showsTab,
    };
  },
};
</script>

<style scoped>
.round-icon-btn {
  width: 2rem;
  height: 2rem;
  padding: 0;
}

.snippet-toggle-btn {
  padding: 0.25rem 0.75rem;
  font-size: 0.8rem;
}

.add-show-step-wrapper {
  overflow: hidden;
  transition: height 0.25s ease;
}

.episode-range-option {
  border: 1px solid var(--surface-border);
  transition: all 0.15s ease;
}

.episode-range-option:hover {
  border-color: var(--primary-color);
}

.episode-range-option-active {
  border-color: var(--primary-color);
  background: rgba(255, 90, 87, 0.08);
}

.episode-range-icon {
  color: var(--primary-color);
}

.episode-range-count-input {
  width: 3.5rem;
}

.episode-range-count-input :deep(input) {
  width: 100%;
  text-align: center;
  padding-left: 0.5rem;
  padding-right: 0.5rem;
}

:deep(.p-calendar .p-datepicker-trigger) {
  background: transparent;
  border: 1px solid #ced4da;
  border-left: none;
  color: #6c757d;
}

:deep(.p-calendar .p-datepicker-trigger:hover) {
  background: rgba(0, 0, 0, 0.04);
  color: #495057;
}

.snippet-audio-player {
  height: 2.25rem;
  max-width: 16rem;
}

.episode-title-cell {
  display: inline-block;
  max-width: 14rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}
</style>
